import InlineTitle from "components/InlineTitle";
import PaginationContainer from "components/PaginationContainer";

import LicenseOverviewContainer from "./components/LicenseOverviewContainer";
import ResultModal from "./components/ResultModal";
import AddLicenseModal from "./components/AddLicenseModal";

// for msp
import LicensesTableMSP from "./components/msp/LicensesTableMSP";
import AssignToModal from "./components/msp/AssignToModal";
import LicensesToolbarBoxMSP from "./components/msp/LicensesToolbarBoxMSP";
import GenerateModal from "./components/msp/GenerateModal";
import LeftButtonGroupMSP from "./components/msp/LeftButtonGroupMSP";
import ActionsModal from "./components/msp/ActionsModal";

// for org
import LeftButtonGroupORG from "./components/org/LeftButtonGroupORG";
import LicensesToolbarBoxORG from "./components/org/LicensesToolbarBoxORG";
import LicensesTableORG from "./components/org/LicensesTableORG";

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectOrgLevel } from "stores/slice/pageContext";
import { useTranslation } from "react-i18next";

import { uiActions } from "stores/slice/ui";

import licensesService from "./service";
import { userPriv, userInvalid } from "utils/privilege.util";

import useConst, { defaultModalStatus } from "./useConst";
import { orderBy, isNil, size, cloneDeep } from "lodash";

import Nuclias from "const/nuclias";
const { organizationType, timeFormat, userType, userPrivilege } = Nuclias;

const defaultPagination = { current: 1, pageLimit: 10, currentLength: 0 };

const Licenses = ({ filteredData, filterData, setMessages }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { translateList } = useConst();
  const { pageContext, lang } = useSelector((store) => store);

  const [pagination, setPagination] = useState(cloneDeep(defaultPagination));
  const [modalStatus, setModalStatus] = useState(cloneDeep(defaultModalStatus));
  const [resultModalStatus, setResultModalStatus] = useState(false);
  const [resultModalList, setResultModalList] = useState([]);

  const [overview, setOverview] = useState({});
  const [dataList, setDataList] = useState([]);

  const [search, setSearch] = useState({});
  const [quickSearchKey, setQuickSearchKey] = useState({ value: null });
  const info = {
    isOrgLevel: useSelector(selectOrgLevel) === organizationType.org ? true : false,
    actions: {
      editInvalid: userPriv(userPrivilege.manager) // && !userPriv(userType.siteTag),
    }
  }
  info.orgId = info.isOrgLevel ? pageContext.selectedOrg.id : pageContext.si.id;

  const init = async () => {
    try {
      dispatch(uiActions.showSpinner());
      setPagination(cloneDeep(defaultPagination));
      const overviewData = await licensesService.getOverviewData(info.orgId);
      setOverview((overview) => overviewData.licenseCount);
      if (filteredData?.who === "licenses") {
        setQuickSearchKey({ value: filteredData.type });
        filterData(null, null);
      } else {
        const params = licensesService.getFilter(info, pagination);
        const licenseData = await licensesService.getLicenseData(info, params);
        if (!isNil(licenseData.total)) {
          setPagination((pagination) => ({
            ...pagination,
            currentLength: licenseData.total,
          }));
        }
        setDataList(licenseData.data);
      }
    } catch (error) {
      console.debug("license init:", error);
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  };

  const getMSPByPage = async (newPagination) => {
    try {
      dispatch(uiActions.showSpinner());
      const params = licensesService.getFilter(
        info,
        newPagination,
        search
      );
      const licenseData = await licensesService.getLicenseData(info, params);
      setPagination((pagination) => ({
        ...pagination,
        currentLength: licenseData.total,
      }));
      setDataList(licenseData.data);
    } catch (error) {
      console.debug("license getMSPByPage:", error);
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  };

  const getMSPBySearch = async (searchParams) => {
    try {
      dispatch(uiActions.showSpinner());
      const newPagination = cloneDeep(pagination);
      newPagination.current = 1;
      const params = licensesService.getFilter(
        info,
        newPagination,
        searchParams
      );
      const licenseData = await licensesService.getLicenseData(info, params);
      newPagination.currentLength = licenseData.total;
      setPagination(newPagination);
      setDataList(licenseData.data);
    } catch (error) {
      console.debug("license getMSPByPage:", error);
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  };

  const handleSearch = useCallback((searchParams) => {
    // console.debug("searchParams", searchParams);
    setSearch((search) => searchParams);
    if (!info.isOrgLevel) getMSPBySearch(searchParams);
  }, []);

  const handleChangePage = (currentPageNum) => {
    const copy = cloneDeep(pagination);
    copy.current = currentPageNum;
    setPagination(copy);
    if (!info.isOrgLevel) getMSPByPage(copy);
  };
  const handleChangePageLimit = (currentPageNum) => {
    const copy = cloneDeep(pagination);
    copy.pageLimit = currentPageNum;
    setPagination(copy);
    if (!info.isOrgLevel) getMSPByPage(copy);
  };

  const changeModalStatus = useCallback((type, status) => {
    const copy = cloneDeep(defaultModalStatus);
    copy[type].status = status;
    setModalStatus((modalStatus) => copy);
  }, []);

  const cancelModal = useCallback(() => {
    setModalStatus(defaultModalStatus);
  }, []);

  const submitModal = (result) => {
    if (size(result) > 0) {
      setResultModalList((resultModalList) => result);
      setResultModalStatus(true);
    }
    init();
  };

  const quickSearch = (key) => {
    setQuickSearchKey({ value: key });
  };

  const translateDataList = async () => {
    const newList = await translateList(cloneDeep(dataList));
    setDataList(newList);
  };

  useEffect(() => {
    if (dataList?.length > 0) translateDataList();
  }, [lang]);

  useEffect(() => {
    if (!isNil(pageContext)) init();
  }, [pageContext]);

  return (
    <div className="layout-container layout-container--column layout-container--fluid">
      <InlineTitle label={t("c9ab10fcd6")} isNonUnderline />

      <LicenseOverviewContainer
        overviewData={overview}
        onSearch={quickSearch}
      />
      {/* MSP LEVEL */}
      {!info.isOrgLevel && (
        <>
          <div className="d-flex justify-content-between">
            <LeftButtonGroupMSP
              {...{ info, modalStatus, changeModalStatus, dataList }}
            />
            <LicensesToolbarBoxMSP
              {...{
                setMessages,
                modalStatus,
                changeModalStatus,
                handleSearch,
                quickSearchKey,
              }}
            />
          </div>
          <LicensesTableMSP
            {...{ info, dataList, pagination, search, setDataList }}
          />

          {modalStatus.generate.status && (
            <GenerateModal {...{ modalStatus, changeModalStatus }} />
          )}

          <AssignToModal
            {...{ modalStatus, cancelModal, submitModal, dataList }}
          />
          <ActionsModal
            {...{ modalStatus, cancelModal, submitModal, dataList }}
          />
        </>
      )}
      {/* ORG LEVEL */}
      {info.isOrgLevel && (
        <>
          <div className="d-flex justify-content-between">
            <LeftButtonGroupORG {...{ modalStatus, changeModalStatus }} />
            <LicensesToolbarBoxORG
              {...{ dataList, setMessages, handleSearch, quickSearchKey }}
            />
          </div>
          <LicensesTableORG
            {...{ dataList, pagination, search, setPagination }}
          />
        </>
      )}

      <PaginationContainer
        total={pagination?.currentLength || 0}
        onPageChange={(currentPageNum) => {
          handleChangePage(currentPageNum);
        }}
        onEntryLimitChange={(currentPageNum) => {
          handleChangePageLimit(currentPageNum);
        }}
        isCurrentPageId={pagination.current}
      />

      {modalStatus.addLicense.status && (
        <AddLicenseModal
          {...{
            modalStatus,
            submitModal,
            cancelModal,
            info,
          }}
        />
      )}

      {size(resultModalList) > 0 && (
        <ResultModal
          {...{
            results: resultModalList,
            resultModalStatus,
            setResultModalStatus,
          }}
        />
      )}
    </div>
  );
};

export default Licenses;
